import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/tickets/navBar";
import { resetPassword } from "../../api/ticketsPlatform/auth";
import Footer from "../../components/tickets/footer";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(false);

  // No token in URL — invalid link
  if (!token) {
    return (
      <div className="bg-[#021610] text-white min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-4">
            <p className="text-4xl">🔗</p>
            <h2 className="text-xl font-bold">Enlace inválido</h2>
            <p className="text-sm text-gray-400">
              Este enlace no es válido o ha expirado.
            </p>
            <button
              onClick={() => navigate("/tickets-store/auth")}
              className="mt-2 px-6 py-2 rounded-xl border border-green-500 text-green-400 text-sm hover:bg-green-500 hover:text-black transition"
            >
              Ir a iniciar sesión
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const validate = () => {
    const e = {};
    if (!form.password || form.password.length < 8)
      e.password = "Mínimo 8 caracteres";
    if (form.password !== form.confirmPassword)
      e.confirmPassword = "Las contraseñas no coinciden";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    try {
      setLoading(true);
      setApiError(null);
      await resetPassword(token, form.password);
      setSuccess(true);
    } catch (err) {
      setApiError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "El enlace ha expirado o no es válido. Solicita uno nuevo.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#021610] text-white min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-16">
        <div className="w-full max-w-md">
          {/* ── SUCCESS ── */}
          {success ? (
            <div className="text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <span className="text-3xl">✓</span>
              </div>
              <div>
                <p className="text-[10px] tracking-widest text-green-400 uppercase mb-2">
                  Contraseña establecida
                </p>
                <h2 className="text-2xl font-bold">¡Todo listo!</h2>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">
                  Tu contraseña ha sido creada correctamente. Ya puedes iniciar
                  sesión con tu email y contraseña.
                </p>
              </div>
              <button
                onClick={() => navigate("/tickets-store/auth")}
                className="w-full py-3 rounded-xl bg-green-500 text-black font-bold tracking-widest uppercase text-sm hover:bg-green-400 transition"
              >
                Iniciar sesión →
              </button>
            </div>
          ) : (
            /* ── FORM ── */
            <div className="space-y-5">
              <div>
                <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
                  Bienvenido a Afrodise
                </p>
                <h1 className="text-2xl font-bold mt-1">Crea tu contraseña</h1>
                <p className="text-sm text-gray-400 mt-2">
                  Elige una contraseña para activar tu cuenta.
                </p>
              </div>

              <Field
                label="Nueva contraseña"
                type="password"
                value={form.password}
                onChange={(v) => {
                  setForm((f) => ({ ...f, password: v }));
                  setErrors((e) => ({ ...e, password: undefined }));
                  setApiError(null);
                }}
                error={errors.password}
                placeholder="Mínimo 8 caracteres"
              />

              <Field
                label="Confirmar contraseña"
                type="password"
                value={form.confirmPassword}
                onChange={(v) => {
                  setForm((f) => ({ ...f, confirmPassword: v }));
                  setErrors((e) => ({ ...e, confirmPassword: undefined }));
                }}
                error={errors.confirmPassword}
                placeholder="Repite tu contraseña"
              />

              {apiError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                  <p className="text-sm text-red-400">{apiError}</p>
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase text-sm transition
                  ${
                    loading
                      ? "bg-green-700 text-black/50 cursor-not-allowed"
                      : "bg-green-500 text-black hover:bg-green-400"
                  }`}
              >
                {loading ? "Guardando..." : "Establecer contraseña →"}
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ── FIELD ── */
function Field({ label, value, onChange, error, placeholder, type = "text" }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] tracking-widest text-gray-400 uppercase">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`bg-[#031c14] border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition
          ${
            error
              ? "border-red-500/60 focus:border-red-500"
              : "border-green-900/40 focus:border-green-500/60"
          }`}
      />
      {error && (
        <p className="text-[10px] text-red-400 tracking-wide">{error}</p>
      )}
    </div>
  );
}
